import { useMemo, useCallback } from 'react';
import { Box, Grid, Typography, MenuItem, TextField } from '@mui/material';
import Chart from '@/components/general/Chart';
import ChartDateFilter from '@/components/general/ChartDateFilter';
import CustomLink from '@/components/general/CustomLink';
import CustomTable from '@/components/general/CustomTable';
import CustomTableCell from '@/components/general/CustomTableCell';
import CustomTableRow from '@/components/general/CustomTableRow';
import ExportButton from '@/components/general/ExportButton';
import PageTitle from '@/components/general/PageTitle';
import TrendDistribution from '@/components/TrendDistribution';
import { HeaderTable } from '@/types';
import { Trend, UsersAnswers, AnswersWeek } from '@/types/api';
import { Paths } from '@/types/routes';
import { DatePeriodFilters, trendFilters } from '@/utils/charts';
import { dateToPeriodFormat, dateToMonthDayFormat } from '@/utils/format';
import styles from './TrendsReportDetail.styles';

type TrendsReportDetailPageProps = {
  trend: Trend | null;
  dateFilter?: DatePeriodFilters;
  setDateFilter: (value: DatePeriodFilters) => void;
  currentWeek?: AnswersWeek | null;
  handleChangeWeek: (value: string) => void;
  weekOptions: any[];
};

const headersArray: HeaderTable<UsersAnswers>[] = [
  { id: 'name', label: 'Name' },
  { id: 'label', label: 'Answer' },
  { id: 'answerDate', label: 'Submitted Date' },
];

const TrendsReportDetailPage = function ({
  trend,
  dateFilter,
  setDateFilter,
  currentWeek,
  handleChangeWeek,
  weekOptions,
}: TrendsReportDetailPageProps): JSX.Element {
  const renderUsersRow = useCallback((row: UsersAnswers) => {
    return (
      <CustomTableRow key={row.name} data-testid="trend-detail-user-list-item">
        <CustomTableCell>{row.name}</CustomTableCell>
        <CustomTableCell>{row.label}</CustomTableCell>
        <CustomTableCell>{dateToPeriodFormat(row.answerDate)}</CustomTableCell>
      </CustomTableRow>
    );
  }, []);

  const trendDetailsComponent = useMemo(() => {
    if (!trend)
      return (
        <Typography variant="h6" color="primary.light">
          Trend report not found
        </Typography>
      );

    const labels =
      trend.answersWeek?.map(
        (answerWeek) =>
          `${dateToMonthDayFormat(answerWeek.startDate)} - ${dateToMonthDayFormat(
            answerWeek.endDate
          )}`
      ) || [];

    const data = trend.answersWeek?.map((answerWeek) => answerWeek.answerAvg) || [];

    return (
      <>
        <Grid sx={styles.chartsContainer} container spacing={2} marginTop={1} maxWidth="100%">
          <Grid item md={12}>
            <Chart labels={labels} data={data} title={trend.label} height="100">
              <Box sx={styles.detailContainer}>
                <ChartDateFilter
                  filters={trendFilters}
                  currentFilter={dateFilter || DatePeriodFilters.SIX_MONTHS}
                  setNewFilter={setDateFilter}
                />
              </Box>
            </Chart>
          </Grid>
          <Grid item md={12} marginTop={5}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={8}>
                <Typography variant="h4" fontWeight={600} color="primary">
                  Distribution by week
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  value={currentWeek?.startDate || ''}
                  onChange={(event) => handleChangeWeek(event.target.value)}
                  select
                  size="small"
                  variant="outlined"
                  label="Select a week"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                >
                  {weekOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            </Grid>
          </Grid>
          <Grid item md={12}>
            <TrendDistribution
              title="Answer distribution"
              options={trend.options || []}
              answers={currentWeek?.answersOptions?.length ? currentWeek.answersOptions || [] : []}
              height="60"
            />
          </Grid>
        </Grid>
        <Typography variant="h5" fontWeight={600} color="primary" paddingTop={3}>
          Users distribution
        </Typography>
        <CustomTable<UsersAnswers>
          data={currentWeek?.usersAnswers?.length ? currentWeek.usersAnswers || [] : []}
          rowRender={renderUsersRow}
          initialOrderBy="answerDate"
          headersArray={headersArray}
        />
      </>
    );
  }, [
    currentWeek,
    dateFilter,
    handleChangeWeek,
    renderUsersRow,
    setDateFilter,
    trend,
    weekOptions,
  ]);

  return (
    <Box>
      <CustomLink to={Paths.TRENDS_REPORT} variant="subtitle1" data-testid="go-back-link">
        go back
      </CustomLink>
      <Box
        display="flex"
        flexWrap="wrap"
        justifyContent="space-between"
        alignItems="center"
        paddingRight={2}
      >
        <PageTitle title="Trend Report Detail" />
        <ExportButton documentTitle={trend?.label} />
      </Box>
      {trendDetailsComponent}
    </Box>
  );
};

export default TrendsReportDetailPage;
